<template>
  <div class="search-rubric-resource-detail form-group">
    <dl class="field-set type-text read-only-indicator">
      <dd class="field-value">
        <svg-icon name="PencilStrike24" kind="indicative" />
        <span>{{ $t("search_rubric_resource_detail_rubric_info_label") }}</span>
      </dd>
    </dl>

    <!-- Location -->
    <cv-form class="location-details">
      <dl class="field-set type-text location">
        <dt class="field-label">{{ $t("search_resource_detail_location_label") }}</dt>
        <dd class="field-value">
          <span class="message">
            {{ rubricData?.location }}
          </span>
        </dd>
      </dl>
    </cv-form>

    <!-- Course Details -->
    <cv-form class="course-details">
      <h5 class="form-header other-information">
        <span>{{ $t("search_resource_detail_other_information_label") }}</span>
      </h5>

      <!-- rubric tags -->
      <dl class="field-set type-text rubric-tags">
        <dt class="field-label">
          {{ $t("grading_rubric_tags_label") }}
        </dt>
        <dd class="field-value">
          <resource-taggings
            :is-edit-mode="false"
            resource-type="Rubric"
            :resource-uuid="rubricData.uuid"
            :hide-empty-indicator="false"
            single-empty-indicator="true"
          />
        </dd>
      </dl>
      <!-- created by -->
      <dl class="field-set type-text created-by">
        <dt class="field-label">{{ $t("search_resource_detail_created_by_label") }}</dt>
        <dd class="field-value">
          <label-component
            kind="creditor"
            :text="createdBy"
            @click="handleProfileClick(createdById, false)"
          />
          <time class="date">{{ createdDate }}</time>
        </dd>
      </dl>
      <!-- modified by -->
      <dl class="field-set type-text modified-by">
        <dt class="field-label">{{ $t("search_resource_detail_edited_label") }}</dt>
        <dd class="field-value">
          <label-component
            kind="creditor"
            :text="modifiedBy"
            @click="handleProfileClick(modifiedById, false)"
          />
          <time class="date">{{ modifiedDate }}</time>
        </dd>
      </dl>
      <match-indicator v-if="matchKeyword" />
      <!-- Keywords -->
      <dl class="field-set type-text keywords">
        <dt class="field-label">{{ $t("search_resource_detail_keywords_label") }}</dt>
        <dd class="field-value">
          <div v-if="showKeywordsTags" class="output-text">
            <keywords :tags="keywords" />
          </div>
          <div v-else class="output-text">
            {{ keywords }}
          </div>
        </dd>
      </dl>

      <div v-if="showUsedIn" class="field-set type-text used-in">
        <AccordionWrapper
          class="accordion-ghost accordion-component"
          @change="accordionActionChange"
        >
          <cv-accordion-item id="accordion-usedIn" key="usedIn" :open="false">
            <template #title>{{ usedInLabel }}</template>
            <template #content>
              <div v-if="hasContextData" class="lesson-card-wrapper">
                <lesson-detail-card
                  v-for="data in contextData"
                  :key="data.uuid"
                  :context-data="data"
                  :search-filters="rubricData.searchFilters"
                />
              </div>
              <span v-else class="message">-</span>
            </template>
          </cv-accordion-item>
        </AccordionWrapper>
      </div>
    </cv-form>
  </div>
</template>

<script lang="ts">
import { mapGetters } from "vuex";
import SettingsSummaryMixin from "@modules/editor/mixin/settings.summary.mixin";
import { RESOURCE_DOMAIN, NAMESPACE } from "@modules/search/service/search.constant";
import { isEmpty } from "@utils/var.check";
import ActivityIconMixin from "@plugins/common/mixin/activity.icon.mixin";
import Keywords from "@plugins/keywords/components/Keywords";
import AuthoringCopilotTemplateSelectionMixin from "@modules/editor/mixin/authoring.copilot.template.selection.mixin";
import SearchProfile from "@modules/search/mixin/search.profile.mixin.ts";
import ResourceTaggings from "@modules/editor/components/resource-taggings/ResourceTaggings.vue";
import resourceApi from "@apis/resource.api";
import LessonDetailCard from "@plugins/common/components/LessonDetailCard.vue";
import ErrorMixin from "@plugins/common/mixin/error.mixin";

export default {
  name: "SearchRubricResourceDetail",
  components: {
    LessonDetailCard,
    Keywords,

    ResourceTaggings,
  },
  mixins: [
    SettingsSummaryMixin,
    ActivityIconMixin,
    AuthoringCopilotTemplateSelectionMixin,
    SearchProfile,
    ErrorMixin,
  ],
  props: {
    rubricData: { type: Object, default: null },
    context: { type: String, required: true },
    selectionMode: { type: String, default: "" },
  },
  emits: ["activitiesSelected"],
  data() {
    return {
      isLoading: true,
      contextData: {},
    };
  },
  computed: {
    ...mapGetters(NAMESPACE.AUTH, ["isStudent", "isTeacher"]),
    emptyText() {
      return "";
    },
    modifiedBy() {
      return this.rubricData?.modifiedBy;
    },
    modifiedById() {
      return this.rubricData?.modifiedById;
    },
    modifiedDate() {
      return this.rubricData?.modifiedDate;
    },
    createdBy() {
      return this.rubricData?.createdBy;
    },
    createdById() {
      return this.rubricData?.createdById;
    },
    createdDate() {
      return this.rubricData?.createdDate;
    },
    showKeywordsTags() {
      return this.rubricData?.keywords && this.rubricData?.keywords.length > 0;
    },
    keywords() {
      return this.rubricData?.keywords ? this.rubricData?.keywords : "-";
    },
    isDisabled() {
      return (this.isPasscodeEnabled || this.isUpcomingAssignment) && this.isStudent;
    },
    matchKeyword() {
      return !isEmpty(this.rubricData?.highlights?.keyword ?? null);
    },
    isMOEResource() {
      return this.rubricData?.resourceDomain === RESOURCE_DOMAIN.MOE;
    },
    hasContextData() {
      return !isEmpty(this.contextData);
    },
    showUsedIn() {
      return !this.isLessonType;
    },
    usedInLabel() {
      const length = this.hasContextData ? this.contextData.length : 0;
      return `${this.$i18n.t("search_resource_detail_used_in_label")} (${length})`;
    },
  },
  created() {
    this.fetchResourceContext();
  },
  methods: {
    fetchResourceContext() {
      const data = {
        uuid: this.rubricData.uuid,
        type: this.rubricData.type,
        raw: true, // get full context info
      };

      resourceApi.fetchResourceContext(data).then((response) => {
        if (response.code !== "ok") {
          this.passiveApiFailHandler();
          return;
        }

        if (response) {
          this.contextData = response.data;
          this.isLoading = false;
        }
      }, this.passiveApiFailHandler);
    },
  },
};
</script>

<style lang="scss">
@use "@styles/layout/z-map" as *;
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.search-rubric-resource-detail {
  .course-details {
    .form-header {
      &.other-information {
        margin-bottom: $spacing-05;
      }
    }
  }

  .resource-taggings {
    .field-group {
      display: block;
    }
  }

  .additional-information {
    @include carbon--type-style("body-long-01");
    display: none;
    width: 100%;
    margin-top: $spacing-05;
    padding: $spacing-05;
    color: $text-01;
    background-color: $ui-background;
    border-radius: 10px;

    .empty-placeholder {
      margin: 0;

      .content {
        margin-bottom: 0;
      }

      .placeholder-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 80px;
        margin: 0;
      }

      svg {
        transform: scale(0.5);
      }
    }

    label {
      @include carbon--type-style("panel-title-01");
      display: flex;
      align-items: center;
      margin-bottom: $spacing-05;
      color: $text-02;

      .sls-icon {
        margin-right: $spacing-03;
      }
    }
  }

  .field-set {
    &:not(.rubric-tags) {
      .field-value {
        // set a smaller value margin-top to minus off margin added in label-component
        margin-top: $spacing-02;
      }
    }
    &.read-only-indicator {
      padding-top: $spacing-03;

      .field-value {
        @include carbon--type-style("helper-text-01");
        display: flex;
        align-items: center;
        gap: $spacing-03;
        color: $text-02;
      }

      .sls-icon {
        min-width: 24px;

        [fill] {
          fill: $icon-02;

          &[fill="#DFE8F9"] {
            opacity: 0.15;
          }
        }
        [stroke] {
          stroke: $icon-02;
        }
      }
    }
    &.created-by {
      .field-value {
        display: flex;
        align-items: center;
        flex-flow: row wrap;
        .date {
          margin-top: $spacing-02;
          flex: 0 0 100%;
        }
      }
    }
    &.modified-by {
      .field-value {
        display: flex;
        align-items: center;
        flex-flow: row wrap;
        .date {
          margin-top: $spacing-02;
          flex: 0 0 100%;
        }
      }
    }

    &.creditedTo {
      .creditedToContent {
        position: relative;
        word-break: break-word;

        &.collapsed {
          max-height: 4rem;
          overflow: hidden;

          > span {
            cursor: pointer;
            .ellipsis-text {
              cursor: pointer;
            }
          }

          &::before {
            content: "";
            position: absolute;
            height: 50%;
            width: 100%;
            bottom: 0;
            background-image: linear-gradient(rgba(255, 255, 255, 0) 50%, rgba($ui-01, 1));
            z-index: z-map("default");
          }
        }
      }

      .btn-toggle-truncation {
        @include carbon--type-style("card-short-01");
        margin-top: $spacing-01;
      }
    }

    &.used-in {
      .card-component {
        width: 100%;
      }
    }
    &:not(.used-in) {
      .label-component {
        span {
          @include carbon--type-style("label-01", true);
          min-width: 2rem;
          padding: 0.375rem $spacing-03; // to increase pill height to 32px
          text-align: center;
          color: $text-01;
        }
      }
      .keywords {
        .bx--tag {
          padding: 0.375rem $spacing-03; // to increase pill height to 32px
        }
      }
    }
  }
}
</style>
